import React from 'react';

import { CardSSR } from '@atlaskit/smart-card/ssr';

import { SainAction } from '../../../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../../../common/utils/sain-feature-control';
import { extractUrlHostname } from '../../../../../extract-url-hostname';
import { useAIAnswerContext } from '../../../../ai-answer-context';
import { isSourceProduct3P } from '../../../../analytics-helpers';
import { onAIAnswerSourceClick } from '../../../../on-ai-answer-source-click';
import type { NLPSearchResultSource } from '../../../../types';
import { SAIN_CITATION_ATTRIBUTE } from '../citation-selectors';

export const InlineSmartLinkCitation = ({
	source,
	sourceIndex,
}: {
	source: NLPSearchResultSource;
	sourceIndex: number;
}): React.JSX.Element | null => {
	const { commonAttributes, callbacks } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const trackClick = (event: React.MouseEvent<HTMLSpanElement>) => {
		const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
		if (!anchor || !event.currentTarget.contains(anchor)) {
			return;
		}
		const isAuxClick = event.type === 'auxclick' && event.button === 1;
		const isContextMenu = event.type === 'contextmenu';
		if (event.type === 'auxclick' && !isAuxClick) {
			return;
		}
		if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
			return;
		}
		const sourceClickEvent = onAIAnswerSourceClick({
			...commonAttributes,
			sourceIndex,
			isCitation: true,
			isAuxClick,
			isContextMenu,
			hostname: extractUrlHostname(source.url),
			is3PSource: isSourceProduct3P(source.product),
		});
		fireAnalyticsEvent({
			...sourceClickEvent,
			attributes: { ...sourceClickEvent.attributes, elementInteracted: 'smartlink' },
		});
		fireSAINUsageEvent({ metCriteria: SainAction.SOURCE_CLICKED, commonAttributes, source });
		callbacks?.onAIAnswerSourceClick?.();
	};

	return (
		<span
			{...{ [SAIN_CITATION_ATTRIBUTE]: 'true' }}
			onClickCapture={trackClick}
			onAuxClickCapture={trackClick}
			onContextMenuCapture={trackClick}
		>
			<CardSSR
				url={source.url}
				appearance="inline"
				placeholder={source.title || source.url}
				disablePreviewPanel
				onClick={(event, data) => {
					event.preventDefault();
					window.open(data?.destinationUrl ?? source.url, '_blank', 'noopener,noreferrer');
				}}
				showHoverPreview
			/>
		</span>
	);
};
